import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { HighlightedText } from '../utils/searchHighlight';

/** Keep the original value available even when the contact column truncates it. */
export const ContactValue = ({ value, label }: { value: string; label: string }) => {
  const id = useId();
  const anchor = useRef<HTMLDivElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pinned = useRef(false);
  const [open, setOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [position, setPosition] = useState({ left: 0, top: 0, width: 320 });

  const show = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const hide = () => {
    clearTimeout(closeTimer.current);
    pinned.current = false;
    setOpen(false);
    setCopyStatus('idle');
  };
  const scheduleHide = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      if (!pinned.current && !anchor.current?.contains(document.activeElement)
        && !popup.current?.contains(document.activeElement)) hide();
    }, 150);
  };

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const rect = anchor.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(320, window.innerWidth - 16);
      const height = popup.current?.offsetHeight ?? 80;
      const below = rect.bottom + 6;
      const top = below + height <= window.innerHeight - 8 ? below : rect.top - height - 6;
      setPosition({ width, left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
        top: Math.max(8, Math.min(top, window.innerHeight - height - 8)) });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, copyStatus]);

  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!anchor.current?.contains(target) && !popup.current?.contains(target)) hide();
    };
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') hide(); };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const copy = async () => {
    let copied = false;
    try {
      await navigator.clipboard.writeText(value);
      copied = true;
    } catch {
      // Older browsers and insecure contexts still support the existing app fallback.
      const previousFocus = document.activeElement as HTMLElement | null;
      const textArea = document.createElement('textarea');
      textArea.value = value;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      try {
        textArea.focus();
        textArea.select();
        copied = document.execCommand('copy');
      } catch {
        copied = false;
      } finally {
        textArea.remove();
        previousFocus?.focus({ preventScroll: true });
      }
    }
    pinned.current = true;
    show();
    setCopyStatus(copied ? 'copied' : 'failed');
  };

  return (
    <div ref={anchor} className="flex min-w-0 items-center gap-1" onMouseEnter={show}
      onMouseLeave={scheduleHide} onFocus={show} onBlur={scheduleHide}>
      <button type="button" title={value} aria-label={`${label}: ${value}`}
        aria-describedby={open ? id : undefined} aria-expanded={open}
        className="min-w-0 flex-1 truncate text-left select-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2F7BFF]"
        onClick={() => { pinned.current = !pinned.current; pinned.current ? show() : hide(); }}>
        <HighlightedText text={value} />
      </button>
      <button type="button" aria-label={`Скопировать ${label.toLowerCase()}: ${value}`}
        title="Скопировать" onClick={copy}
        className="shrink-0 rounded p-1 text-[#868C98] hover:bg-[#EEF0F2] hover:text-[#1F2A44] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2F7BFF]">
        {copyStatus === 'copied' ? <CheckIcon className="h-4 w-4" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
      </button>
      {open && createPortal(
        <div ref={popup} id={id} role="tooltip" onMouseEnter={show} onMouseLeave={scheduleHide}
          className="fixed z-[100] rounded-lg border border-[#DDE1E7] bg-white p-3 text-[14px] text-[#1F2A44] shadow-lg"
          style={position}>
          <div className="break-all select-text">{value}</div>
          <div role="status" aria-live="polite" className="mt-1 text-[12px] text-[#667085]">
            {copyStatus === 'copied' ? 'Скопировано' : copyStatus === 'failed'
              ? 'Не удалось скопировать. Выделите полное значение и скопируйте вручную.' : 'Полное значение'}
          </div>
        </div>, document.body,
      )}
    </div>
  );
};
